/**
 * Type definitions for Auto Fleet Generator
 * 
 * These types define the configuration, data structures, and results
 * used throughout the fleet generation process.
 */

import React from 'react';
import { OperatingPeriod } from '../../../../types/simulation';
import { Vehicle, Fleet } from '../FleetMenu';
import { Layer } from '../../../../context/context';
import type { MixedFleetSummary } from '../../../../utils/fixedRoutes/fleetTypes';

/**
 * Generator mode - determines input source
 */
export type GeneratorMode = 'trip-layer' | 'manual' | 'fixed-routes' | 'mixed';

/**
 * Peak period definition for break scheduling
 */
export interface PeakPeriod {
  start: string; // HH:MM format
  end: string;   // HH:MM format
}

/**
 * Break schedule entry with timing in minutes from midnight
 */
export interface BreakSchedule {
  startMinutes: number;
  endMinutes: number;
  durationMinutes: number;
}

/**
 * Generated shift data for a single vehicle
 */
export interface GeneratedShift {
  vehicleId: string;
  vehicleName: string;
  operatingPeriods: OperatingPeriod[];
}

/**
 * Main configuration for the fleet generator
 */
export interface GeneratorConfig {
  // Mode selection
  mode: GeneratorMode;
  
  // Trip layer analysis (when mode = 'trip-layer')
  selectedLayerId?: string;
  
  // Manual input (when mode = 'manual')
  manualVehicleCount?: number;
  
  // Fixed routes (when mode = 'fixed-routes')
  selectedRouteIds?: string[];
  vehicleSharingMode: 'dedicated' | 'shared'; // Default: 'shared'
  
  // Mixed fleet (when mode = 'mixed')
  priorityMode?: 'fixed-first' | 'on-demand-first' | 'equal' | 'weighted';
  fixedRouteWeight?: number; // 0-100, required if priorityMode = 'weighted'
  onDemandWeight?: number; // 0-100, required if priorityMode = 'weighted'
  transitionTimeMinutes?: number; // Time between service type transitions (default: 0)
  
  // Service hours
  serviceStartTime: string; // HH:MM format
  serviceEndTime: string;   // HH:MM format
  
  // Shift constraints
  enableShiftAllocation?: boolean; // Toggle to enable/disable shift allocation (default: false)
  maxShiftTimeMinutes: number;
  breakLengthMinutes: number;
  minTimeBeforeBreakMinutes: number;
  minTimeAfterBreakMinutes: number;
  
  // Vehicle configuration
  standardSeats: number;
  wheelchairSeats: number;
  startLocationId: string;
  endLocationId: string;
  
  // Peak periods
  peakPeriods: PeakPeriod[];
  breakReductionDuringPeakMinutes: number;
  
  // Advanced options
  staggerBreaks: boolean;
  tripsPerVehiclePerHour: number;
  demandCurve?: Record<number, number>; // Hour (0-23) -> demand value
  hourlyVehicleRequirements?: Record<number, number>; // Hour (0-23) -> vehicles needed
  useDemandBasedAllocation: boolean; // Reduce vehicles during low demand
  
  // Fleet flexibility
  flexibilityPercent: number; // -50 to +100
}

/**
 * Result from analyzing a trip layer
 */
export interface TripAnalysisResult {
  totalTrips: number;
  hourlyDemand: Record<number, number>; // Hour (0-23) -> trip count
  peakHour: number; // Hour with most trips
  peakDemand: number; // Max trips in any hour
  suggestedServiceStart: string; // HH:MM
  suggestedServiceEnd: string;   // HH:MM
  calculatedVehicles: number;
  hourlyVehicleRequirements?: Record<number, number>; // Hour (0-23) -> vehicles needed
  detectedPeakPeriods?: Array<{ start: string; end: string; demand: number }>;
}

/**
 * Complete generated fleet data ready for creation
 */
export interface GeneratedFleetData {
  fleetName: string;
  vehicles: Vehicle[];
  shifts: GeneratedShift[];
  summary: {
    totalVehicles: number;
    totalShifts: number;
    totalBreaks: number;
    servicePeriod: string; // e.g., "06:00 - 22:00"
    totalStandardSeats: number;
    totalWheelchairSeats: number;
    totalCapacity: number;
    activeVehicleHours: number; // Total hours vehicles are actively working (excluding breaks)
    totalVehicleHours: number; // Total available vehicle hours (service duration * vehicles)
    utilizationPercent: number; // activeVehicleHours / totalVehicleHours * 100
  };
  mixedFleetSummary?: MixedFleetSummary; // Present when mode is 'mixed'
  editedShifts?: GeneratedShift[];
}

/**
 * Validation result for input checking
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Props for the main modal component
 */
export interface AutoFleetGeneratorModalProps {
  open: boolean;
  onClose: () => void;
  fleets: Fleet[];
  setFleets: React.Dispatch<React.SetStateAction<Fleet[]>>;
  layers: Layer[];
  availablePoints: Array<{ id: string; name: string; coordinates?: [number, number] }>;
  onFleetCreated?: (fleet: Fleet) => void;
}

/**
 * Props for step components
 */
export interface ModeSelectionStepProps {
  config: GeneratorConfig;
  onConfigChange: (config: Partial<GeneratorConfig>) => void;
  layers: Layer[];
  onAnalysisComplete?: (result: TripAnalysisResult) => void;
}

export interface ConfigurationStepProps {
  config: GeneratorConfig;
  onConfigChange: (config: Partial<GeneratorConfig>) => void;
  availablePoints: Array<{ id: string; name: string; coordinates?: [number, number] }>;
  analysisResult?: TripAnalysisResult;
}

export interface PreviewStepProps {
  generatedFleetData: GeneratedFleetData | null;
  fleetName: string;
  onFleetNameChange: (name: string) => void;
  onRegenerate: () => void | Promise<void>;
  peakPeriods: PeakPeriod[];
  mode?: GeneratorMode; // Mode to determine which visualization to show
  interactive?: boolean;
  editedSource?: 'generated' | 'edited';
  onBlockClick?: (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => void;
  onResetEdits?: () => void;
}

/**
 * Props for visualization components
 */
export interface FleetTimelineVisualizationProps {
  vehicles: GeneratedShift[];
  serviceStartMinutes: number;
  serviceEndMinutes: number;
  intervalMinutes: number; // 15 or 30
  peakPeriods: PeakPeriod[];
  interactive?: boolean;
  onBlockClick?: (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => void;
  /**
   * Optional render-prop to replace the default vehicle-name cell in the left
   * column. Use when the consumer needs richer per-vehicle controls inline
   * with the timeline.
   */
  renderVehicleCell?: (vehicle: GeneratedShift, vehicleIndex: number) => React.ReactNode;
  /**
   * Override the left vehicle column width (default 180px). Use a wider value
   * when paired with `renderVehicleCell` to fit richer controls.
   */
  vehicleColumnWidthPx?: number;
  /**
   * Optional render-prop to render a trailing cell on the right of each
   * vehicle row (e.g. a delete button). When set, the grid grows by one
   * column of width `rowSuffixWidthPx`.
   */
  renderVehicleRowSuffix?: (vehicle: GeneratedShift, vehicleIndex: number) => React.ReactNode;
  /**
   * Width of the trailing row-suffix column (default 48px). Only used when
   * `renderVehicleRowSuffix` is set.
   */
  rowSuffixWidthPx?: number;
}

export interface FleetSummaryCardsProps {
  summary: GeneratedFleetData['summary'];
  mixedFleetSummary?: MixedFleetSummary;
  mode?: GeneratorMode;
}

/**
 * Internal state for shift generator
 */
export interface ShiftGeneratorState {
  config: GeneratorConfig;
  serviceStartMinutes: number;
  serviceEndMinutes: number;
  serviceDurationMinutes: number;
  effectiveVehicleCount: number;
}

